import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Trash2, Send, AlertTriangle, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import DocumentsPanel from "@/components/DocumentsPanel";
import ConfirmAction from "@/components/ConfirmAction";
import { toast } from "sonner";
import { fmtDate } from "@/lib/utils";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function VendorInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bulkTax, setBulkTax] = useState("");

  const load = () => api.get(`/vendor/invoices/${id}`).then((r) => setInv(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!inv) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;
  const editable = inv.status === "draft";

  const setField = (k, v) => setInv({ ...inv, [k]: v });
  const setLine = (idx, k, v) => {
    const lines = inv.lines.map((l, i) => (i === idx ? { ...l, [k]: v } : l));
    setInv({ ...inv, lines });
  };
  const addLine = () => setInv({ ...inv, lines: [...inv.lines, { description: "", quantity: 1, unit_price: 0, tax_rate: 0, po_item_ref: null }] });
  const removeLine = (idx) => setInv({ ...inv, lines: inv.lines.filter((_, i) => i !== idx) });
  // Convenience: stamp one rate on every line (still editable per line).
  const applyTaxToAll = () => {
    const rate = Number(bulkTax);
    if (Number.isNaN(rate) || rate < 0 || rate > 100) return toast.error("Enter a tax rate between 0 and 100");
    setInv({ ...inv, lines: inv.lines.map((l) => ({ ...l, tax_rate: rate })) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        invoice_number: inv.invoice_number, invoice_date: inv.invoice_date || null, due_date: inv.due_date || null,
        delivery_date: inv.delivery_date || null, tracking_number: inv.tracking_number || null, carrier: inv.carrier || null,
        pay_link_url: inv.pay_link_url || null, notes: inv.notes || null,
        lines: inv.lines.map((l) => ({
          description: l.description, quantity: Number(l.quantity) || 0, unit_price: Number(l.unit_price) || 0,
          tax_rate: Number(l.tax_rate) || 0, po_item_ref: l.po_item_ref ?? null,
        })),
      };
      const r = await api.patch(`/vendor/invoices/${id}`, body);
      setInv(r.data);
      setWarnings(r.data.warnings || []);
      toast.success("Draft saved");
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const sendInvoice = async () => {
    const saved = await save();
    if (!saved) return;
    try {
      const r = await api.post(`/vendor/invoices/${id}/send`);
      setInv(r.data);
      setWarnings(r.data.warnings || []);
      toast.success(`Invoice ${r.data.invoice_number} sent to the contractor`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  // Tax is per line: each line's tax % applies to that line's amount only
  // (mirrors routers/vendor.py _price_line, rounded the same way).
  const round2 = (n) => Math.round(n * 100) / 100;
  const lineAmount = (l) => round2((Number(l.quantity) || 0) * (Number(l.unit_price) || 0));
  const lineTax = (l) => round2(lineAmount(l) * (Number(l.tax_rate) || 0) / 100);
  const subtotal = round2(inv.lines.reduce((s, l) => s + lineAmount(l), 0));
  const taxTotal = round2(inv.lines.reduce((s, l) => s + lineTax(l), 0));
  const total = round2(subtotal + taxTotal);

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="vendor-invoice-detail-page">
      <Link to="/vendor/invoices" className="flex items-center gap-1 text-[13px] text-slate-500 hover:text-slate-800"><ChevronLeft size={15} /> Invoices</Link>
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-extrabold tracking-tight mono">{inv.invoice_number}</h1>
          <StatusBadge status={inv.status} />
        </div>
        {editable && (
          <div className="flex gap-2">
            <button onClick={() => setConfirmDelete(true)} disabled={saving} data-testid="delete-draft-invoice-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold text-red-600 hover:bg-red-50"><Trash2 size={14} /> Delete</button>
            <button onClick={save} disabled={saving} data-testid="save-draft-button" className="px-4 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">Save draft</button>
            <button onClick={sendInvoice} disabled={saving} data-testid="send-invoice-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-sky-500 text-white text-[13px] font-semibold hover:bg-sky-600"><Send size={14} /> Send invoice</button>
          </div>
        )}
      </div>
      <div className="text-[13px] text-slate-500 mt-1">
        Against PO <button onClick={() => navigate(`/vendor/orders/${inv.po_id}`)} className="mono text-sky-600 font-medium hover:underline">{inv.po_number}</button>
      </div>

      {inv.status === "paid" && (
        <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-100 px-3.5 py-2.5 text-[12.5px] text-emerald-800" data-testid="vendor-paid-banner">
          Paid by the contractor{inv.paid_at ? ` on ${fmtDate(inv.paid_at)}` : ""}{inv.paid_method ? ` (${inv.paid_method.replace(/_/g, " ")})` : ""}.
        </div>
      )}
      {inv.correction_message && inv.status === "draft" && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-[12.5px] text-amber-800" data-testid="correction-banner">
          <b>Correction requested:</b> {inv.correction_message}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3.5 py-3 text-[12.5px] text-amber-800" data-testid="invoice-warnings">
          <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={14} /> Quantity warning</div>
          {warnings.map((w, i) => <div key={i} className="mt-1">{w}</div>)}
          <div className="mt-1 text-amber-600">You can still send — partial shipments across several invoices are normal, but double-check these lines.</div>
        </div>
      )}

      <div className="mt-6 grid sm:grid-cols-3 gap-3 text-[12.5px]">
        {[
          ["Invoice #", "invoice_number", "text"], ["Invoice date", "invoice_date", "date"], ["Due date", "due_date", "date"],
          ["Delivery date", "delivery_date", "date"], ["Tracking #", "tracking_number", "text"], ["Carrier", "carrier", "text"],
        ].map(([label, key, type]) => (
          <div key={key} className="bg-white rounded-xl border border-slate-100 p-3.5">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
            {editable ? (
              <input type={type} value={inv[key] || ""} onChange={(e) => setField(key, e.target.value)} data-testid={`invoice-${key}-input`} className="mt-1.5 w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]" />
            ) : (
              <div className="mt-1 font-medium text-slate-700">{key.includes("date") && inv[key] ? fmtDate(inv[key]) : (inv[key] || "—")}</div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white rounded-xl border border-slate-100 p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-[15px] font-bold">Lines</h2>
          {editable && (
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1.5 text-[12.5px] text-slate-500">
                <label htmlFor="bulk-tax">Tax % for all lines</label>
                <input
                  id="bulk-tax" type="number" min="0" max="100" step="0.001" value={bulkTax}
                  onChange={(e) => setBulkTax(e.target.value)} placeholder="e.g. 8.25" data-testid="bulk-tax-input"
                  className="w-20 h-8 px-2 rounded-lg border border-slate-200 text-right text-[13px]"
                />
                <button onClick={applyTaxToAll} data-testid="apply-tax-all-button" className="font-semibold text-sky-600 hover:text-sky-700">Apply</button>
              </div>
              <button onClick={addLine} data-testid="add-line-button" className="flex items-center gap-1 text-[12.5px] font-semibold text-sky-600 hover:text-sky-700"><Plus size={14} /> Add line</button>
            </div>
          )}
        </div>
        <table className="w-full mt-3 text-[13px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2">Description</th><th className="text-right py-2 w-24">Qty</th><th className="text-right py-2 w-32">Unit Price</th><th className="text-right py-2 w-28">Amount</th><th className="text-right py-2 w-24">Tax %</th><th className="text-right py-2 w-24">Tax</th>{editable && <th className="w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l, idx) => (
              <tr key={idx} className="border-b border-slate-50" data-testid={`invoice-line-${idx}`}>
                <td className="py-2 pr-2">
                  {editable ? <input value={l.description} onChange={(e) => setLine(idx, "description", e.target.value)} className="w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]" /> : l.description}
                </td>
                <td className="py-2 text-right">
                  {editable ? <input type="number" min="0" step="any" value={l.quantity} onChange={(e) => setLine(idx, "quantity", e.target.value)} className="w-20 h-9 px-2 rounded-lg border border-slate-200 text-right text-[13px]" /> : l.quantity}
                </td>
                <td className="py-2 text-right">
                  {editable ? <input type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => setLine(idx, "unit_price", e.target.value)} className="w-28 h-9 px-2 rounded-lg border border-slate-200 text-right text-[13px]" /> : fmt(l.unit_price)}
                </td>
                <td className="py-2 text-right mono">{fmt(lineAmount(l))}</td>
                <td className="py-2 text-right">
                  {editable ? (
                    <input
                      type="number" min="0" max="100" step="0.001" value={l.tax_rate ?? 0}
                      onChange={(e) => setLine(idx, "tax_rate", e.target.value)}
                      aria-label={`Tax percent for line ${idx + 1}`} data-testid={`invoice-line-${idx}-tax-rate`}
                      className="w-20 h-9 px-2 rounded-lg border border-slate-200 text-right text-[13px]"
                    />
                  ) : (
                    <span className="mono">{Number(l.tax_rate) ? `${Number(l.tax_rate)}%` : "—"}</span>
                  )}
                </td>
                <td className="py-2 text-right mono text-slate-500" data-testid={`invoice-line-${idx}-tax`}>{fmt(editable ? lineTax(l) : (l.tax_amount ?? lineTax(l)))}</td>
                {editable && <td className="py-2 text-right"><button onClick={() => removeLine(idx)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 flex justify-end text-[13px]">
          <div className="w-56 space-y-1.5">
            <div className="flex justify-between text-slate-500"><span>Subtotal</span><span className="mono">{fmt(editable ? subtotal : inv.subtotal)}</span></div>
            <div className="flex justify-between text-slate-500">
              <span>Tax <span className="text-[11px] text-slate-400">(sum of lines)</span></span>
              <span className="mono" data-testid="invoice-tax-total">{fmt(editable ? taxTotal : inv.tax_total)}</span>
            </div>
            <div className="flex justify-between font-bold border-t border-slate-100 pt-1.5"><span>Total</span><span className="mono" data-testid="invoice-total">{fmt(editable ? total : inv.total)}</span></div>
          </div>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Payment link (your merchant account)</div>
          {editable ? (
            <input value={inv.pay_link_url || ""} onChange={(e) => setField("pay_link_url", e.target.value)} placeholder="https://pay.yourcompany.com/inv/123" data-testid="invoice-pay-link-input" className="mt-1.5 w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]" />
          ) : inv.pay_link_url ? (
            <a href={inv.pay_link_url} target="_blank" rel="noopener noreferrer" className="mt-1 flex items-center gap-1 text-sky-600 text-[13px] font-medium hover:underline">{inv.pay_link_url} <ExternalLink size={12} /></a>
          ) : (
            <div className="mt-1 text-slate-400 text-[13px]">None</div>
          )}
          <div className="mt-1.5 text-[11.5px] text-slate-400">The contractor pays through your own payment provider — ScapeSource never processes funds.</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Notes</div>
          {editable ? (
            <textarea value={inv.notes || ""} onChange={(e) => setField("notes", e.target.value)} rows={2} data-testid="invoice-notes-input" className="mt-1.5 w-full px-2.5 py-2 rounded-lg border border-slate-200 text-[13px]" />
          ) : (
            <div className="mt-1 text-slate-600 text-[13px]">{inv.notes || "—"}</div>
          )}
        </div>
      </div>

      <div className="mt-5">
        <DocumentsPanel entityType="invoice" entityId={inv.id} />
      </div>
      <ConfirmAction open={confirmDelete} onOpenChange={setConfirmDelete} title={`Delete draft ${inv.invoice_number || ""}?`}
        description="It was never sent to your customer, so it's removed completely." confirmLabel="Delete" testId="delete-invoice-dialog"
        onConfirm={async () => { await api.delete(`/vendor/invoices/${inv.id}`); toast.success("Draft invoice deleted"); navigate(`/vendor/orders/${inv.po_id}`); }} />
    </div>
  );
}
